import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Filter } from "lucide-react";

export const NONE_TOKEN = "__NONE__";

/**
 * MultiSelect with checkboxes + an optional "Select All" master checkbox.
 *
 * Props:
 *   options: [[value, label], ...] (optional 3rd elem = disabled)
 *   value: array of selected values.
 *   onChange: (nextArray) => void
 *   label: string — shown on the trigger button
 *   testid, icon, fit, placeholder
 *   defaultAll: when true, an EMPTY value renders as ALL boxes checked (the default "all"
 *     = no filter). Unchecking "Select All" clears the boxes and stores [NONE_TOKEN], which
 *     the parent treats as "show nothing until at least one is picked". Label drops "All ".
 *   hideSelectAll: hide the master "Select All" row.
 */
export default function MultiSelect({ options, value = [], onChange, label, testid, icon: Icon, fit = false, placeholder, defaultAll = false, hideSelectAll = false, compact = false, neutral = false, renderSub = null, renderTrailing = null, size = "sm" }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const norm = options.map((o) => ({ v: o[0], lbl: o[1], disabled: !!o[2] }));
    const enabled = norm.filter((o) => !o.disabled);
    const total = enabled.length;

    const real = value.filter((v) => v !== NONE_TOKEN);
    const showAll = defaultAll && value.length === 0;             // default = everything checked
    const isNone = defaultAll && value.length === 1 && value[0] === NONE_TOKEN;
    const isChecked = (v) => (showAll ? true : real.includes(v));
    const allSelected = showAll || (total > 0 && enabled.every((o) => real.includes(o.v)));
    const selectedCount = showAll ? total : real.length;

    const toggle = (v, disabled) => {
        if (disabled) return;
        const base = showAll ? enabled.map((o) => o.v) : real;
        const next = base.includes(v) ? base.filter((x) => x !== v) : [...base, v];
        if (next.length === total && enabled.every((o) => next.includes(o.v))) { onChange([]); return; } // all → clean default
        if (next.length === 0) { onChange(defaultAll ? [NONE_TOKEN] : []); return; }
        onChange(next);
    };

    const toggleAll = () => {
        if (allSelected) onChange(defaultAll ? [NONE_TOKEN] : []);
        else onChange(defaultAll ? [] : enabled.map((o) => o.v));
    };

    const IconComponent = Icon || Filter;
    const xs = size === "xs";
    const sizeCls = xs ? "py-1.5 px-2 text-xs" : `${compact ? "h-10" : "h-11"} px-2.5 text-sm`;
    const iconCls = xs ? "h-3 w-3" : "h-3.5 w-3.5";
    const emptyText = placeholder || (defaultAll ? "None" : `All ${label}`);
    const allText = defaultAll ? label : `All ${label}`;
    const displayText =
        showAll ? allText :
        isNone || selectedCount === 0 ? (defaultAll ? "None" : emptyText) :
        allSelected ? allText :
        selectedCount === 1 ? (norm.find((o) => o.v === real[0])?.lbl || real[0]) :
        `${selectedCount} selected`;
    const isDefault = showAll || displayText === `All ${label}` || displayText === emptyText;
    // `neutral` keeps the trigger looking like its empty state (white/grey) even when
    // a value is selected — used for the Athlete Profile Position box.
    const dim = isDefault || neutral;

    return (
        <div className={fit ? "relative inline-block" : "relative"} ref={ref}>
            <button
                type="button"
                data-testid={testid}
                onClick={() => setOpen((o) => !o)}
                className={`${fit ? "w-auto" : "w-full"} ${sizeCls} rounded-none bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500] transition-colors flex items-center gap-1.5 text-left`}
            >
                <IconComponent className={`${iconCls} shrink-0 ${dim ? "text-[#a0a0a0]" : "text-[#ff4500]"}`} />
                <span className={`${fit ? "whitespace-nowrap" : "flex-1 truncate"} ${dim ? "" : "text-[#ff4500]"}`}>{displayText}</span>
                <ChevronDown className={`${iconCls} shrink-0 transition-transform ${open ? "rotate-180" : ""} ${dim ? "text-[#a0a0a0]" : "text-[#ff4500]"}`} />
            </button>

            {open && (
                <div
                    data-testid={`${testid}-menu`}
                    className="absolute z-[9999] mt-1 w-max min-w-full max-w-[92vw] bg-[#141414] border border-[#2a2a2a] shadow-xl max-h-72 overflow-y-auto"
                >
                    {!hideSelectAll && (
                        <button
                            type="button"
                            data-testid={`${testid}-select-all`}
                            onClick={toggleAll}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-white bg-[#0f0f0f] hover:bg-[#1a1a1a] border-b border-[#2a2a2a]"
                        >
                            <span className={`h-4 w-4 border flex items-center justify-center ${allSelected ? "bg-[#ff4500] border-[#ff4500]" : "border-[#3a3a3a] bg-transparent"}`}>
                                {allSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                            </span>
                            Select All
                        </button>
                    )}
                    {norm.map((o) => {
                        const checked = isChecked(o.v);
                        return (
                            <div key={o.v}>
                            <div className="flex items-stretch">
                            <button
                                type="button"
                                data-testid={`${testid}-option-${o.v}`}
                                onClick={() => toggle(o.v, o.disabled)}
                                disabled={o.disabled}
                                title={o.disabled ? "Not available for your current profile" : undefined}
                                className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 text-sm text-left ${
                                    o.disabled
                                        ? "text-[#5a5a5a] cursor-not-allowed"
                                        : "text-white hover:bg-[#1a1a1a] cursor-pointer"
                                }`}
                            >
                                <span className={`h-4 w-4 border flex items-center justify-center shrink-0 ${checked ? "bg-[#ff4500] border-[#ff4500]" : "border-[#3a3a3a] bg-transparent"}`}>
                                    {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                </span>
                                <span className="flex-1 truncate">{o.lbl}</span>
                                {o.disabled && (
                                    <span className="text-[9px] uppercase tracking-[0.15em] text-[#5a5a5a] ml-2">Locked</span>
                                )}
                            </button>
                            {renderTrailing && renderTrailing(o.v, checked)}
                            </div>
                            {renderSub && renderSub(o.v, checked)}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
